import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Modal from '../../components/Modal';
import {
  deleteCoupon,
  listCoupons,
  setCouponActive,
  upsertCoupon,
  assignCouponToUser,
  type CouponRecord,
  type OrderType,
} from '../../services/coupons';
import { searchUsers, type UserSummary } from '../../services/users';

const couponSchema = z.object({
  code: z
    .string()
    .min(3, 'Kod zorunludur')
    .max(32)
    .transform((value) => value.toUpperCase()),
  title: z.string().min(3, 'Başlık zorunludur'),
  description: z.string().optional(),
  discountType: z.enum(['percentage', 'fixed']),
  discountValue: z.coerce.number().positive("İndirim tutarı 0'dan büyük olmalıdır"),
  minimumOrder: z.coerce.number().optional().or(z.literal('')).transform((val) => (val === '' ? null : Number(val))),
  validFrom: z.string().optional().or(z.literal('')),
  validUntil: z.string().optional().or(z.literal('')),
  usageLimit: z.coerce.number().optional().or(z.literal('')).transform((val) => (val === '' ? null : Number(val))),
  usagePerUser: z.coerce.number().optional().or(z.literal('')).transform((val) => (val === '' ? null : Number(val))),
  isActive: z.boolean().default(true),
  orderTypes: z.array(z.enum(['delivery', 'takeaway', 'dine_in'])).min(1, 'En az bir sipariş tipi seçilmelidir'),
});

type CouponFormValues = z.infer<typeof couponSchema>;

const DEFAULT_VALUES: CouponFormValues = {
  code: '',
  title: '',
  description: '',
  discountType: 'percentage',
  discountValue: 5,
  minimumOrder: null,
  validFrom: '',
  validUntil: '',
  usageLimit: null,
  usagePerUser: null,
  isActive: true,
  orderTypes: ['delivery', 'takeaway', 'dine_in'],
};

function formatDiscount(coupon: CouponRecord) {
  return coupon.discountType === 'percentage'
    ? `${coupon.discountValue}%`
    : `₺${coupon.discountValue.toFixed(2)}`;
}

function formatDate(value: string | null) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('tr-TR', { timeZone: 'Europe/Istanbul' });
}

const ORDER_TYPE_LABELS: Record<OrderType, string> = {
  delivery: 'Teslimat',
  takeaway: 'Gel-al',
  dine_in: 'Restoran',
};

export default function CouponManager() {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<CouponRecord | null>(null);
  const [userSearch, setUserSearch] = useState('');
  const [userResults, setUserResults] = useState<UserSummary[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserSummary | null>(null);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [grantCount, setGrantCount] = useState<number>(1);

  const {
    data: coupons = [],
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ['marketing', 'coupons'],
    queryFn: listCoupons,
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CouponFormValues>({
    resolver: zodResolver(couponSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const upsertMutation = useMutation({
    mutationFn: upsertCoupon,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['marketing', 'coupons'] });
      closeModal();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (coupon: CouponRecord) => deleteCoupon(coupon.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['marketing', 'coupons'] });
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async (coupon: CouponRecord) => setCouponActive(coupon.id, !coupon.isActive),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['marketing', 'coupons'] });
    },
  });

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingCoupon(null);
    setSelectedUser(null);
    setUserSearch('');
    setUserResults([]);
    setGrantCount(1);
    reset(DEFAULT_VALUES);
  }, [reset]);

  const openCreate = () => {
    setEditingCoupon(null);
    setSelectedUser(null);
    setUserSearch('');
    setUserResults([]);
    setGrantCount(1);
    reset(DEFAULT_VALUES);
    setModalOpen(true);
  };

  const openEdit = (coupon: CouponRecord) => {
    setEditingCoupon(coupon);
    setSelectedUser(null);
    setUserSearch('');
    setUserResults([]);
    setGrantCount(1);
    reset({
      code: coupon.code,
      title: coupon.title,
      description: coupon.description ?? '',
      discountType: coupon.discountType,
      discountValue: coupon.discountValue,
      minimumOrder: coupon.minimumOrder,
      validFrom: coupon.validFrom?.slice(0, 10) ?? '',
      validUntil: coupon.validUntil?.slice(0, 10) ?? '',
      usageLimit: coupon.usageLimit,
      usagePerUser: coupon.usagePerUser,
      isActive: coupon.isActive,
      orderTypes: coupon.orderTypes ?? ['delivery', 'takeaway', 'dine_in'],
    });
    setModalOpen(true);
  };

  const onSubmit = (values: CouponFormValues) => {
    const payload = {
      id: editingCoupon?.id,
      code: values.code,
      title: values.title,
      description: values.description?.trim() || null,
      discountType: values.discountType,
      discountValue: Number(values.discountValue),
      minimumOrder: values.minimumOrder === null ? null : Number(values.minimumOrder),
      validFrom: values.validFrom ? new Date(values.validFrom).toISOString() : null,
      validUntil: values.validUntil ? new Date(values.validUntil).toISOString() : null,
      usageLimit: values.usageLimit === null ? null : Number(values.usageLimit),
      usagePerUser: values.usagePerUser === null ? null : Number(values.usagePerUser),
      isActive: values.isActive,
      orderTypes: values.orderTypes as OrderType[],
      isUserSpecific: Boolean(selectedUser) || Boolean(editingCoupon?.isUserSpecific),
    };

    return upsertMutation.mutateAsync(payload).then(async (savedCoupon) => {
      if (selectedUser) {
        await assignCouponToUser(savedCoupon.id, selectedUser.id, grantCount);
      }
      return savedCoupon;
    });
  };

  useEffect(() => {
    const searchValue = userSearch.trim();
    if (searchValue.length < 2) {
      setUserResults([]);
      setUserSearchLoading(false);
      return;
    }

    let isActive = true;
    setUserSearchLoading(true);
    const timer = setTimeout(async () => {
      try {
        const results = await searchUsers(searchValue);
        if (isActive) {
          setUserResults(results);
        }
      } catch (error) {
        console.error('Kullanıcı arama hatası:', error);
      } finally {
        if (isActive) {
          setUserSearchLoading(false);
        }
      }
    }, 250);

    return () => {
      isActive = false;
      clearTimeout(timer);
    };
  }, [userSearch]);

  return (
    <>
      <div className="card">
        <div className="card-header">
          <div>
            <p className="card-title">Aktif kampanyalar</p>
            <p className="card-subtitle">
              {isLoading || isFetching ? 'Kupon verileri senkronize ediliyor...' : `Toplam ${coupons.length} kupon`}
            </p>
          </div>
          <button type="button" className="button primary" onClick={openCreate}>
            + Kupon Ekle
          </button>
        </div>

        {coupons.length === 0 ? (
          <div className="empty-state">
            <strong>Henüz kupon yok</strong>
            <p>Başlangıç için hoş geldin indirimi veya sadakat kuponu oluşturun.</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Kod</th>
                  <th>İndirim</th>
                  <th>Geçerlilik</th>
                  <th>Kurallar</th>
                  <th>Durum</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {coupons.map((coupon) => (
                  <tr key={coupon.id}>
                    <td>
                      <strong>{coupon.code}</strong>
                      <p className="helper-text">{coupon.title}</p>
                    </td>
                    <td>{formatDiscount(coupon)}</td>
                    <td>
                      <div className="helper-text">
                        <div>Başlangıç: {formatDate(coupon.validFrom)}</div>
                        <div>Bitiş: {formatDate(coupon.validUntil)}</div>
                      </div>
                    </td>
                    <td>
                      <div className="helper-text">
                        {coupon.minimumOrder ? <div>Minimum sipariş ₺{coupon.minimumOrder.toFixed(2)}</div> : null}
                        {coupon.usageLimit !== null && coupon.usageLimit !== undefined ? (
                          <div>
                            Toplam kullanım {Math.max(0, coupon.usedCount ?? 0)}/{coupon.usageLimit}
                          </div>
                        ) : null}
                        {coupon.usagePerUser ? <div>Kişi başı {coupon.usagePerUser}</div> : null}
                        {coupon.isUserSpecific ? <div>Kişiye özel kupon</div> : null}
                        <div>
                          Geçerli sipariş tipi:{' '}
                          {coupon.orderTypes && coupon.orderTypes.length > 0
                            ? coupon.orderTypes.map((type) => ORDER_TYPE_LABELS[type]).join(' • ')
                            : 'Tümü'}
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`chip ${coupon.isActive ? 'success' : 'warning'}`}>
                        {coupon.isActive ? 'Aktif' : 'Durduruldu'}
                      </span>
                    </td>
                    <td>
                      <div className="table-actions">
                        <button type="button" className="button secondary" onClick={() => openEdit(coupon)}>
                          Düzenle
                        </button>
                        <button
                          type="button"
                          className="button ghost"
                          onClick={() => toggleActiveMutation.mutate(coupon)}
                        >
                          {coupon.isActive ? 'Durdur' : 'Aktifleştir'}
                        </button>
                        <button
                          type="button"
                          className="button danger"
                          onClick={() => {
                            if (window.confirm(`${coupon.code} kuponunu silmek istediğinize emin misiniz?`)) {
                              deleteMutation.mutate(coupon);
                            }
                          }}
                        >
                          Sil
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={editingCoupon ? `${editingCoupon.code} kuponunu güncelle` : 'Yeni kupon oluştur'}
        wide
      >
        <form className="form-grid" onSubmit={handleSubmit(onSubmit)}>
          <div className="form-row">
            <label className="form-field">
              <span>Kod</span>
              <input type="text" placeholder="HOSGELDIN10" {...register('code')} />
              {errors.code ? <span className="error-text">{errors.code.message}</span> : null}
            </label>
            <label className="form-field">
              <span>Başlık</span>
              <input type="text" placeholder="Hoş geldin indirimi" {...register('title')} />
              {errors.title ? <span className="error-text">{errors.title.message}</span> : null}
            </label>
          </div>

          <label className="form-field">
            <span>Açıklama</span>
            <textarea placeholder="Personel için görünen açıklama" {...register('description')} />
          </label>

          <div className="form-row">
            <label className="form-field">
              <span>İndirim tipi</span>
              <select {...register('discountType')}>
                <option value="percentage">Yüzde</option>
                <option value="fixed">Sabit tutar</option>
              </select>
            </label>
            <label className="form-field">
              <span>Tutar</span>
              <input type="number" step="0.5" min="0" {...register('discountValue')} />
              {errors.discountValue ? <span className="error-text">{errors.discountValue.message}</span> : null}
            </label>
          </div>

          <div className="form-row">
            <label className="form-field">
              <span>Minimum sipariş (opsiyonel)</span>
              <input type="number" step="1" min="0" {...register('minimumOrder')} />
            </label>
            <label className="form-field">
              <span>Toplam kullanım sınırı</span>
              <input type="number" min="0" {...register('usageLimit')} />
            </label>
            <label className="form-field">
              <span>Kişi başı kullanım</span>
              <input type="number" min="0" {...register('usagePerUser')} />
            </label>
          </div>

          <div className="form-row">
            <label className="form-field">
              <span>Geçerlilik başlangıcı</span>
              <input type="date" {...register('validFrom')} />
            </label>
            <label className="form-field">
              <span>Geçerlilik bitişi</span>
              <input type="date" {...register('validUntil')} />
            </label>
          </div>

          <div className="form-field">
            <span>Geçerli sipariş tipleri</span>
            <div className="chip-row">
              {(['delivery', 'takeaway', 'dine_in'] as OrderType[]).map((type) => (
                <label key={type} className="form-field checkbox inline-checkbox">
                  <input type="checkbox" value={type} {...register('orderTypes')} />
                  <span>{ORDER_TYPE_LABELS[type]}</span>
                </label>
              ))}
            </div>
            {errors.orderTypes ? <span className="error-text">{errors.orderTypes.message}</span> : null}
            <span className="helper-text">Sipariş tipi seçmezseniz varsayılan olarak tümü geçerlidir.</span>
          </div>

          <div className="form-field">
            <span>Kullanıcıya özel kupon (opsiyonel)</span>
            <div className="form-row center">
              <input
                type="text"
                placeholder="İsim veya e-posta ile ara"
                value={userSearch}
                onChange={(event) => setUserSearch(event.target.value)}
              />
              {selectedUser ? (
                <button type="button" className="button ghost" onClick={() => setSelectedUser(null)}>
                  Seçimi temizle
                </button>
              ) : null}
            </div>
            {selectedUser ? (
              <p className="helper-text">
                Seçili kullanıcı: <strong>{selectedUser.fullName ?? 'İsimsiz kullanıcı'}</strong> ({selectedUser.email})
              </p>
            ) : null}
            {!selectedUser && userSearchLoading ? (
              <p className="helper-text">Kullanıcılar aranıyor...</p>
            ) : null}
            {!selectedUser && !userSearchLoading && userResults.length > 0 ? (
              <div className="chip-row">
                {userResults.map((user) => (
                  <button
                    key={user.id}
                    type="button"
                    className="chip"
                    onClick={() => {
                      setSelectedUser(user);
                      setUserSearch(`${user.fullName ?? 'İsimsiz kullanıcı'} (${user.email})`);
                    }}
                  >
                    {user.fullName ?? 'İsimsiz kullanıcı'} • {user.email}
                  </button>
                ))}
              </div>
            ) : null}
            {!selectedUser && !userSearchLoading && userSearch.trim().length >= 2 && userResults.length === 0 ? (
              <p className="helper-text">Eşleşen kullanıcı bulunamadı.</p>
            ) : null}

            <label className="form-field">
              <span>Tanımlanacak kullanım hakkı</span>
              <input
                type="number"
                min="1"
                value={grantCount}
                disabled={!selectedUser}
                onChange={(event) => {
                  const parsed = Number(event.target.value);
                  setGrantCount(Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 1);
                }}
              />
            </label>
            <span className="helper-text">
              Kullanıcı seçerseniz kupon sadece bu kişiye tanımlanır ve belirlediğiniz kadar kullanılabilir.
            </span>
          </div>

          <label className="form-field checkbox inline-checkbox">
            <input type="checkbox" {...register('isActive')} />
            <span>Kupon aktif</span>
          </label>

          <div className="form-actions">
            <button type="button" className="button ghost" onClick={closeModal}>
              İptal
            </button>
            <button type="submit" className="button primary" disabled={isSubmitting || upsertMutation.isPending}>
              {editingCoupon ? 'Kuponu kaydet' : 'Kupon oluştur'}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}
